import { type ReactNode, useEffect, useMemo, useState } from "react";
import { ClipboardList, Gauge, Pause, Play, Plus, ShieldAlert, Timer } from "lucide-react";
import { calculateOperationalKpis } from "../../../domain/simulation";
import { SERVICE_DAY_LABELS, dayMinuteToLabel, serviceDayForDate } from "../../../domain/rules/schedule";
import type { ActiveOpsDay, FlightInstance, GlobalEvent, SaveGame, ScheduleSource, WorldData } from "../../../domain/types";
import { formatMoney } from "../../format";
import { PanelHeader } from "../../components/Panel";
import { KpiTile } from "./PlayerShared";
import { releaseQueueFlights, tryBuildOpsDayPreview } from "./playerData";
import { formatFlightInstanceStatus } from "./PlayerTimeline";


export function OverviewView(props: {
  save: SaveGame;
  world: WorldData;
  schedule: ScheduleSource;
  kpis: ReturnType<typeof calculateOperationalKpis>;
  dailyProfit: number;
  openCustomFlightCount: number;
  activeEvents: GlobalEvent[];
  onStartOpsDay: () => void;
  onTickOps: (minutes: number) => void;
  onCreateGame: () => void;
}) {
  const [autoClock, setAutoClock] = useState(false);
  const ops = props.save.activeOpsDay;
  const activeFlights = ops?.flights.filter((flight) => !["landed", "cancelled", "failed"].includes(flight.status)).length ?? 0;
  const releaseQueue = useMemo(
    () => releaseQueueFlights(props.save.activeOpsDay ?? tryBuildOpsDayPreview(props.save, props.schedule, props.activeEvents, props.world)),
    [props.activeEvents, props.save, props.schedule, props.world]
  );
  const releaseQueuePreview = !props.save.activeOpsDay;
  useEffect(() => {
    if (!autoClock || !ops) return;
    const timer = window.setInterval(() => props.onTickOps(30), 30_000);
    return () => window.clearInterval(timer);
  }, [autoClock, ops?.id, props.onTickOps]);
  const blockers = props.save.operationsLog
    .slice(0, 8)
    .flatMap((operation) => operation.notes.map((note) => `${operation.title}: ${note}`))
    .slice(0, 5);
  return (
    <section className="player-view overview-layout">
      <section className="panel overview-kpi-panel">
        <PanelHeader icon={<Gauge size={15} />} title="运行 KPI / A+15" code="KPI" />
        <div className="kpi-grid">
          <KpiTile label="到达正点率" value={`${props.kpis.arrivalOnTimeRate}%`} tone={arrivalOnTimeTone(props.kpis.arrivalOnTimeRate)} />
          <KpiTile label="取消率" value={`${props.kpis.cancellationRate}%`} tone={cancellationRateTone(props.kpis.cancellationRate)} />
          <KpiTile label="平均到达延误" value={`${props.kpis.averageArrivalDelayMinutes}m`} tone={averageArrivalDelayTone(props.kpis.averageArrivalDelayMinutes)} />
          <KpiTile label="受影响衔接" value={String(props.kpis.propagatedConnections)} tone={propagatedConnectionTone(props.kpis.propagatedConnections)} />
          <KpiTile label="上一日利润" value={formatMoney(props.dailyProfit)} tone={dailyProfitTone(props.dailyProfit)} />
          <KpiTile label="自定义待办" value={String(props.openCustomFlightCount)} tone={openCustomFlightTone(props.openCustomFlightCount)} />
        </div>
      </section>

      <section className="panel overview-action-panel">
        <PanelHeader icon={<Play size={15} />} title="运行日控制" code="DAY" />
        <div className="control-stack">
          <button className="primary-command" onClick={primaryOpsAction(ops, props.onTickOps, props.onStartOpsDay)}>
            <Play size={16} />
            {primaryOpsLabel(ops)}
          </button>
          <div className="row-actions">
            <button disabled={!ops} onClick={() => props.onTickOps(15)}>+15m</button>
            <button disabled={!ops} onClick={() => props.onTickOps(60)}>+60m</button>
            <button disabled={!ops} onClick={() => setAutoClock((value) => !value)}>
              {autoClockIcon(autoClock)}
              {autoClockLabel(autoClock)}
            </button>
          </div>
          <button className="command" onClick={props.onCreateGame}>
            <Plus size={15} />
            重建席位
          </button>
          <div className="mini-readout">
            <span>当前日期</span>
            <strong>
              D{props.save.day} / {SERVICE_DAY_LABELS[serviceDayForDate(props.save.dateIso)]}
            </strong>
          </div>
          <div className="mini-readout">
            <span>运行时钟</span>
            <strong>{opsClockLabel(ops)}</strong>
          </div>
          <div className="mini-readout">
            <span>追踪航班</span>
            <strong>{trackedFlightLabel(ops, activeFlights, releaseQueue.length)}</strong>
          </div>
        </div>
      </section>

      <section className="panel overview-plan-panel">
        <PanelHeader icon={<ClipboardList size={15} />} title="待放行列表" code="REL" />
        <ReleaseQueueList flights={releaseQueue} preview={releaseQueuePreview} />
      </section>

      <section className="panel overview-alert-panel">
        <PanelHeader icon={<ShieldAlert size={15} />} title="风险和事件" code="ALRT" />
        <ul className="message-list">
          {props.activeEvents.map((event) => (
            <li key={event.id}>{event.title}</li>
          ))}
          {blockers.map((item) => (
            <li key={item}>{item}</li>
          ))}
          {props.activeEvents.length === 0 && blockers.length === 0 && <li>当前没有活动事件或运行阻断。</li>}
        </ul>
      </section>
    </section>
  );
}

function ReleaseQueueList({ flights, preview }: { flights: FlightInstance[]; preview: boolean }) {
  return (
    <div className="release-queue-list">
      {flights.map((flight) => (
        <article key={flight.id} className={`release-queue-card ${flight.status}`}>
          <div className="release-queue-main">
            <strong>{flight.flightNo}</strong>
            <span>
              {flight.origin}-{flight.destination}
              {technicalStopSuffix(flight.technicalStop)}
            </span>
          </div>
          <em>{dayMinuteToLabel(flight.estimatedDepartureMinute)} - {dayMinuteToLabel(flight.estimatedArrivalMinute)}</em>
          <span className="release-queue-status">{releaseQueueStatusLabel(preview, flight.status)}</span>
          <small>
            {flight.sourceTitle} / {flight.aircraftId ?? "未派机"}
          </small>
          {renderReleaseDraftSummary(flight)}
        </article>
      ))}
      {flights.length === 0 && <div className="empty-state">{releaseQueueEmptyText(preview)}</div>}
    </div>
  );
}

function renderReleaseDraftSummary(flight: FlightInstance) {
  const draft = flight.releaseDraft;
  if (!draft) {
    return null;
  }
  return (
    <small className={releaseDraftClassName(draft.blockers)}>
      草案 R{draft.revision} / {draft.aircraftCandidates.length} 架候选
      {releaseDraftBlockerSuffix(draft.blockers)}
    </small>
  );
}

type KpiTone = "good" | "bad" | "warn";

function arrivalOnTimeTone(arrivalOnTimeRate: number): KpiTone {
  if (arrivalOnTimeRate >= 85) {
    return "good";
  }
  return "bad";
}

function cancellationRateTone(cancellationRate: number): KpiTone {
  if (cancellationRate === 0) {
    return "good";
  }
  return "bad";
}

function averageArrivalDelayTone(averageArrivalDelayMinutes: number): KpiTone {
  if (averageArrivalDelayMinutes <= 15) {
    return "good";
  }
  return "warn";
}

function propagatedConnectionTone(propagatedConnections: number): KpiTone {
  if (propagatedConnections === 0) {
    return "good";
  }
  return "warn";
}

function dailyProfitTone(dailyProfit: number): KpiTone {
  if (dailyProfit >= 0) {
    return "good";
  }
  return "bad";
}

function openCustomFlightTone(openCustomFlightCount: number): KpiTone {
  if (openCustomFlightCount === 0) {
    return "good";
  }
  return "warn";
}

function primaryOpsAction(ops: ActiveOpsDay | undefined, onTickOps: (minutes: number) => void, onStartOpsDay: () => void): () => void {
  if (ops) {
    return () => onTickOps(30);
  }
  return onStartOpsDay;
}

function primaryOpsLabel(ops: ActiveOpsDay | undefined): string {
  if (ops) {
    return "推进 30 分钟";
  }
  return "开始运行日";
}

function autoClockIcon(autoClock: boolean): ReactNode {
  if (autoClock) {
    return <Pause size={14}/>;
  }
  return <Timer size={14}/>;
}

function autoClockLabel(autoClock: boolean): string {
  if (autoClock) {
    return "暂停自动";
  }
  return "自动时钟";
}

function opsClockLabel(ops: ActiveOpsDay | undefined): string {
  if (ops) {
    return dayMinuteToLabel(ops.currentMinute);
  }
  return "未启动";
}

function trackedFlightLabel(ops: ActiveOpsDay | undefined, activeFlights: number, releaseQueueLength: number): string {
  if (ops) {
    return `${activeFlights}/${ops.flights.length}`;
  }
  return String(releaseQueueLength);
}

function technicalStopSuffix(technicalStop: string | undefined): string {
  if (technicalStop) {
    return ` via ${technicalStop}`;
  }
  return "";
}

function releaseQueueStatusLabel(preview: boolean, status: FlightInstance["status"]): string {
  if (preview) {
    return "预生成";
  }
  return formatFlightInstanceStatus(status);
}

function releaseQueueEmptyText(preview: boolean): string {
  if (preview) {
    return "暂无可预生成的待放行航班。";
  }
  return "当前没有待放行航班。";
}

function releaseDraftClassName(blockers: string[]): string {
  if (blockers.length > 0) {
    return "release-queue-warning";
  }
  return "";
}

function releaseDraftBlockerSuffix(blockers: string[]): string {
  if (blockers.length > 0) {
    return ` / 阻断 ${blockers[0]}`;
  }
  return "";
}
